import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import className from 'classnames';
import { useSelector } from 'react-redux';
import { Icon } from '../../../components';
import { SlideGroupContext } from '../../../components/SlideManager';
import { ABUtils, Config, isTrainsRoundTripDiscountCard, TrackingUtils } from '../../../utils';
import { EntryPoint, TripType } from '../../../models';
import { getTripType, isTrainsProductActive } from '../../../redux/currentSearch/selectors';

// eslint-disable-next-line complexity
const Field = ({ errorMessage, ErrorMessageComponent, id, icon, placeholder, value, isDisabled, renderValue }) => {
    const context = useContext(SlideGroupContext);
    const tripType = useSelector((state) => getTripType(state));
    const isTrainsProduct = useSelector((state) => isTrainsProductActive(state));
    const isRailsRoundTripDiscountCard = isTrainsRoundTripDiscountCard({ isTrainsProduct, tripType });
    const { isCssVariationMobile } = ABUtils.getABs();

    const direction = Config.get('isRtl') ? 'rtl' : 'ltr';
    const currentEntryPoint = Config.get('entryPoint');
    const isRoundTrip = tripType === TripType.ROUND_TRIP;
    const iconStyle = direction === 'rtl' ? { left: '-16px' } : { right: '-16px' };
    const invalidClassName = className({
        'invalid': !!errorMessage && !isCssVariationMobile,
    });
    const iconClassName = className({
        'odf-icon-2xl odf-input-icon odf-input-inline-icon odf-clickable': !isCssVariationMobile,
        'odf-icon-left odf-text-mono-color-06 odf-space-inner-left-xs': !isCssVariationMobile,
        'prisma-icon-group-left': isCssVariationMobile,
        'odf-icon-bidi': true,
    });
    const inputGroupClassName = className({
        'odf-input-group odf-input-group-left odf-space-outer-top-s': !isCssVariationMobile,
        'prisma-input-group': isCssVariationMobile,
        'odf-space-outer-bottom-s': !errorMessage && isCssVariationMobile && id !== 'DEPARTURE',
        'invalid odf-space-outer-bottom-m': !!errorMessage && isCssVariationMobile,
        'od-input-group': isCssVariationMobile,
        'od-compact-margin': !errorMessage && isCssVariationMobile && id === 'DEPARTURE',
    });
    const inputClassName = className({
        'odf-input-l odf-text-lg odf-input odf-clickable odf-text-ellipsis': !isCssVariationMobile,
        'prisma-input': isCssVariationMobile,
        'od-compact-input-pax-padding': isCssVariationMobile && ( id === 'PAX' || id === 'ROOM') && isRoundTrip && !isRailsRoundTripDiscountCard,
        'od-compact-input-dates-pr': isCssVariationMobile && id === 'DATE' && isRoundTrip,
        'od-compact-input-height': isCssVariationMobile,
        'od-compact-input-top': isCssVariationMobile && id === 'DEPARTURE',
        'od-compact-input-bottom': isCssVariationMobile && id === 'ARRIVAL',
        'od-compact-input-radius': isCssVariationMobile && id !== 'DEPARTURE' && id !== 'ARRIVAL',
    });

    const onFieldClick = () => {
        if (!isDisabled) {
            context.open(id);
            trackFieldClick({ id, value, entryPoint: currentEntryPoint });
        }
    };

    return (
        <div className={invalidClassName}>
            <div
                className={inputGroupClassName}
                onClick={onFieldClick}
            >
                {renderValue != null ? (
                    <div
                        id={id}
                        data-testid={id}
                        className={inputClassName}
                        role="textbox"
                        tabIndex={isDisabled ? -1 : 0}
                        aria-readonly="true"
                        aria-placeholder={placeholder || undefined}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                        }}
                    >
                        {renderValue}
                    </div>
                ) : (
                    <input
                        type="text"
                        id={id}
                        data-testid={id}
                        className={inputClassName}
                        value={value}
                        readOnly
                        placeholder={placeholder}
                        disabled={isDisabled}
                        style={{ ...(isDisabled ? { color: '#AAAAAA' } : {}) }}
                    />
                )}
                {!isCssVariationMobile && (
                    <span>
                        <Icon
                            style={{ zIndex: 0 }}
                            glyph={icon}
                            className={iconClassName}
                        />
                    </span>
                )}
                {isCssVariationMobile && (
                    <>
                        <label className="prisma-label prisma-label-valid">
                            <span className="prisma-inner-label od-inner-label">{placeholder}</span>
                        </label>
                        {!!errorMessage && (
                            <>
                                <div className="prisma-form-icons" style={iconStyle}>
                                    <span className="odf-space-outer-right-m prisma-input-icon prisma-input-icon-clear
                                        odf-icon odf-icon-cross-circle-light"
                                    />
                                    <span className="odf-space-outer-right-m prisma-input-icon prisma-input-icon-valid
                                        odf-icon odf-icon-tick-circle"
                                    />
                                    <span className="odf-space-outer-right-m prisma-input-icon prisma-input-icon-error
                                        odf-icon odf-icon-exclamation-circle"
                                    />
                                </div>
                                {ErrorMessageComponent ? (
                                    <ErrorMessageComponent errorMessage={errorMessage} dataTestId={id} />
                                ) : (
                                    <div className="prisma-labels odf-text-sm" data-testid="room-selector-children-error">
                                        <div className="odf-row-fluid odf-strong prisma-label-error">
                                            {errorMessage}
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </>
                )}
            </div>
            {!isCssVariationMobile && !!errorMessage && ErrorMessageComponent && (
                <ErrorMessageComponent errorMessage={errorMessage} dataTestId={id} />
            )}
            {!isCssVariationMobile && !!errorMessage && !ErrorMessageComponent && (
                <span className="odf-label odf-text-sm odf-space-outer-xs" data-testid="room-selector-children-error">
                    {errorMessage}
                </span>
            )}
        </div>
    );
};

function getTrackingLabel({ trackingField, value }) {
    if (trackingField === 'orig' || trackingField === 'dest') {
        return value && value.length > 0
            ? `searchbox_dest_edited:${trackingField}`
            : `edit_search_click_open_${trackingField}`;
    }
    if (trackingField === 'calendar' || trackingField === 'pass') {
        return `edit_search_click_open_${trackingField}`;
    }
    return null;
}

function trackFieldClick({ id, value, entryPoint }) {
    const TRACKING_FIELD_MAP = {
        DEPARTURE: 'orig',
        ARRIVAL: 'dest',
        DATE: 'calendar',
        PAX: 'pass'
    };
    const trackingField = TRACKING_FIELD_MAP[id];
    const isTrainsOrFlights = entryPoint === EntryPoint.TRAINS || entryPoint === EntryPoint.FLIGHTS;
    if (!trackingField || !isTrainsOrFlights) return;

    const action = entryPoint === EntryPoint.FLIGHTS ? 'searcher_flights' : 'searcher_trains';
    const label = getTrackingLabel({ trackingField, value });
    if (label) {
        TrackingUtils.trackEvent({ action, label });
    }
}

Field.propTypes = {
    errorMessage: PropTypes.string,
    ErrorMessageComponent: PropTypes.func,
    icon: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
    placeholder: PropTypes.string,
    value: PropTypes.string.isRequired,
    isDisabled: PropTypes.bool,
    renderValue: PropTypes.node,
};

Field.defaultProps = {
    errorMessage: '',
    placeholder: '',
    isDisabled: false,
    renderValue: null,
};

export default Field;
